import React, { useCallback, useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  TrendingUp, Users, ShoppingCart, Wallet, Percent, Receipt,
  MapPin, Building2, Package, Layers, Radio, Smartphone, Calendar, RefreshCw, FileDown,
  MousePointer, Search, BarChart2, Activity,
  Lightbulb, Target, Sparkles, DollarSign, Apple, Split,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Gürgençler — Dönemsel Performans Raporu (Temmuz / Ağustos / Eylül)
//
// Tüm veriler GA4'e CANLI istek atılarak gelir (backend: /api/gurgencler-report
// /quarter). Glowytics DB'sine hiçbir şey yazılmaz/okunmaz.
// ---------------------------------------------------------------------------

const API = 'https://api.glowytics.com/api/gurgencler-report/quarter';

const nfInt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
const nfTRY = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 });
const pct = (v) => `%${(Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;

const KPI = [
  { key: 'sessions', label: 'Oturum (Trafik)', icon: TrendingUp, accent: 'emerald', fmt: nfInt.format },
  { key: 'users', label: 'Kullanıcı', icon: Users, accent: 'sky', fmt: nfInt.format },
  { key: 'transactions', label: 'Sipariş', icon: ShoppingCart, accent: 'violet', fmt: nfInt.format },
  { key: 'revenue', label: 'Ciro', icon: Wallet, accent: 'amber', fmt: nfTRY.format },
  { key: 'conversionRate', label: 'Dönüşüm Oranı', icon: Percent, accent: 'rose', fmt: (v) => pct(v) },
  { key: 'aov', label: 'Ort. Sepet (AOV)', icon: Receipt, accent: 'cyan', fmt: nfTRY.format },
];

const ACCENT = {
  emerald: 'from-emerald-50 to-white text-emerald-700 ring-emerald-200/70',
  sky: 'from-sky-50 to-white text-sky-700 ring-sky-200/70',
  violet: 'from-violet-50 to-white text-violet-700 ring-violet-200/70',
  amber: 'from-amber-50 to-white text-amber-700 ring-amber-200/70',
  rose: 'from-rose-50 to-white text-rose-700 ring-rose-200/70',
  cyan: 'from-cyan-50 to-white text-cyan-700 ring-cyan-200/70',
};

function Card({ title, icon: Icon, children, hint }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
        {Icon && <Icon className="w-4 h-4 text-gray-500" />}
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {hint && <span className="ml-auto text-[11px] text-gray-400">{hint}</span>}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

function Table({ columns, rows, render }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
          {columns.map((c, i) => (
            <th key={i} className={`px-5 py-2.5 font-medium ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-5 py-6 text-center text-gray-400">Veri yok</td></tr>
        ) : rows.map((r, i) => render(r, i))}
      </tbody>
    </table>
  );
}

const td = (extra = '') => `px-5 py-2.5 ${extra}`;
const tdRight = 'px-5 py-2.5 text-right tabular-nums';
const fmtX = (v) => `${(Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}x`;
const fmtDur = (secs) => {
  const s = Math.round(Number(secs) || 0); const m = Math.floor(s / 60);
  return m > 0 ? `${m}dk ${s % 60}sn` : `${s}sn`;
};

// Dönemler arası ciro geçişini gösteren ok+yüzde rozeti.
function DeltaChip({ pct, launch }) {
  if (pct == null) return <span className="text-gray-300 text-xl px-1">→</span>;
  const up = pct >= 0;
  return (
    <div className="flex flex-col items-center justify-center px-1">
      {launch && <span className="text-[9px] font-bold text-orange-600 whitespace-nowrap mb-0.5">🚀 iPhone lansmanı</span>}
      <span className={`text-2xl leading-none ${up ? 'text-emerald-500' : 'text-rose-500'}`}>→</span>
      <span className={`text-xs font-bold whitespace-nowrap ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
        {up ? '+' : ''}{pct.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
      </span>
    </div>
  );
}

// Dönem Momentumu — raporun "bizim tarzımız" hero'su: Tem→Ağu→Eyl1→Eyl2 ciro
// akışı, geçişler oklarla vurgulanır, Eylül lansmana göre 2'ye bölünür.
function PeriodMomentum({ periods }) {
  if (!periods?.length) return null;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
        <Activity className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-gray-800">Dönem Momentumu — Ciro Geçişleri</h3>
        <span className="ml-auto text-[11px] text-gray-400">Eylül, iPhone lansmanına göre 2'ye bölündü</span>
      </div>
      <div className="flex items-stretch overflow-x-auto px-5 py-5 gap-0">
        {periods.map((p, i) => {
          const isLaunchTransition = p.key === 'eyl2';
          const sep = p.key === 'eyl1' || p.key === 'eyl2';
          return (
            <React.Fragment key={p.key}>
              {i > 0 && <DeltaChip pct={p.revDeltaPct} launch={isLaunchTransition} />}
              <div className={`shrink-0 w-44 rounded-xl p-4 ring-1 ${sep ? 'bg-orange-50 ring-orange-200' : 'bg-slate-50 ring-slate-200'}`}>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold text-gray-800">{p.short}</span>
                  {p.tag && <span className={`text-[9px] font-semibold ${p.key === 'eyl2' ? 'text-orange-600' : 'text-amber-600'}`}>{p.tag}</span>}
                </div>
                <div className="text-xl font-bold text-gray-900 mt-2">{nfTRY.format(p.revenue)}</div>
                <div className="text-[11px] text-gray-500">{nfInt.format(p.transactions)} sipariş · AOV {nfTRY.format(p.aov)}</div>
                <div className="text-[10px] text-gray-400 mt-1.5 pt-1.5 border-t border-gray-200/70">
                  Mac <b className="text-gray-600">{nfInt.format(p.mac.qty)}</b> · iPad <b className="text-gray-600">{nfInt.format(p.ipad.qty)}</b> adet
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

// "Bizim yorumumuz" — bölüm altına gömülen görüş kartı (narrative katmanı).
function NoteCallout({ children, tone = 'indigo' }) {
  const tones = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`flex gap-2.5 items-start px-5 py-3 border-t ${tones[tone]} text-[13px] leading-relaxed`}>
      <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
      <p><span className="font-semibold">Bizim yorumumuz: </span>{children}</p>
    </div>
  );
}

export default function GurgenclerReport() {
  const now = new Date();
  const defaultYear = now.getFullYear() - 1;
  const [year, setYear] = useState(defaultYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const handleExport = useCallback(async (y) => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      // Sayfayı olduğu gibi yakala (Türkçe karakterler tarayıcı tarafından
      // render edildiği için PDF'te bozulmaz). Butonlar `.pdf-hide` ile atlanır.
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#f9fafb',
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.classList && el.classList.contains('pdf-hide'),
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const img = canvas.toDataURL('image/png');

      let heightLeft = imgH;
      let position = margin;
      pdf.addImage(img, 'PNG', margin, position, imgW, imgH);
      heightLeft -= (pageH - margin * 2);
      while (heightLeft > 0) {
        position -= (pageH - margin * 2);
        pdf.addPage();
        pdf.addImage(img, 'PNG', margin, position, imgW, imgH);
        heightLeft -= (pageH - margin * 2);
      }
      pdf.save(`gurgencler-q3-${y}.pdf`);
    } catch (err) {
      setError(`PDF oluşturulamadı: ${err.message}`);
    } finally {
      setExporting(false);
    }
  }, []);

  const fetchData = useCallback(async (y) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}?year=${y}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
    } catch (err) {
      setError(err.message || 'Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(year); }, [fetchData, year]);

  const yearOptions = [defaultYear, defaultYear - 1, defaultYear - 2];

  return (
    <div className="min-h-screen bg-gray-50">
      <div ref={reportRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-emerald-600 mb-1">
              <Radio className="w-3.5 h-3.5" /> GA4 · canlı veri
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Gürgençler — Dönemsel Performans</h1>
            <p className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="font-semibold text-gray-800">{data?.period?.label || `1 Temmuz – 30 Eylül ${year}`}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                Back-to-School (BTS) dönemi
              </span>
            </p>
            <p className="text-xs text-gray-400 mt-1">Mac/iPad izolasyonu · Landing page · Meta vs Google · ROAS & bütçe önerisi</p>
          </div>
          <div className="pdf-hide flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y} · 1 Tem – 30 Eyl</option>)}
            </select>
            <button
              onClick={() => fetchData(year)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </button>
            <button
              onClick={() => handleExport(year)}
              disabled={exporting || !data}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              <FileDown className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} /> {exporting ? 'Hazırlanıyor…' : 'PDF İndir'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-32">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Yönetici Özeti — bizim hikâyemiz, raporun en üstünde */}
            {data.insights?.summary?.length > 0 && (
              <section className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-md overflow-hidden">
                <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-base font-semibold">Yönetici Özeti — BTS {data.year}</h2>
                  <span className="ml-auto text-[11px] text-white/50">{data.period?.label}</span>
                </div>
                <ul className="px-6 py-4 space-y-2.5">
                  {data.insights.summary.map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-slate-100">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Dönem Momentumu hero — bizim tarzımız, geçişler vurgulu */}
            {data.periods?.length > 0 && <PeriodMomentum periods={data.periods} />}

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {KPI.map(({ key, label, icon: Icon, accent, fmt }) => (
                <div key={key} className={`rounded-2xl bg-gradient-to-b ring-1 ${ACCENT[accent]} p-4 shadow-sm`}>
                  <Icon className="w-5 h-5 mb-2 opacity-80" />
                  <div className="text-[11px] font-medium opacity-70">{label}</div>
                  <div className="text-lg font-bold text-gray-900 mt-0.5">{fmt(data.totals[key])}</div>
                </div>
              ))}
            </div>

            {/* Dönem Kırılımı — Eylül 2'ye bölünmüş, Δ önceki döneme göre */}
            <Card title="Dönem Kırılımı" icon={Calendar} hint="Eylül lansmana göre 1–15 / 16–30 olarak bölündü · Δ = önceki döneme göre ciro">
              <Table
                columns={[
                  { label: 'Dönem' }, { label: 'Oturum', align: 'right' }, { label: 'Sipariş', align: 'right' },
                  { label: 'Mac', align: 'right' }, { label: 'iPad', align: 'right' },
                  { label: 'Ciro', align: 'right' }, { label: 'Δ ciro', align: 'right' },
                ]}
                rows={data.periods || []}
                render={(p) => {
                  const isLaunch = p.key === 'eyl1' || p.key === 'eyl2';
                  return (
                    <tr key={p.key} className={`hover:bg-gray-50/60 ${isLaunch ? 'bg-orange-50/40' : ''}`}>
                      <td className={td('font-medium text-gray-800')}>
                        {p.label}
                        {p.tag && <span className={`ml-2 text-[10px] rounded px-1.5 py-0.5 ${p.key === 'eyl2' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>{p.tag}</span>}
                      </td>
                      <td className={tdRight}>{nfInt.format(p.sessions)}</td>
                      <td className={tdRight}>{nfInt.format(p.transactions)}</td>
                      <td className={tdRight}>{nfInt.format(p.mac.qty)}</td>
                      <td className={tdRight}>{nfInt.format(p.ipad.qty)}</td>
                      <td className={`${tdRight} font-semibold text-gray-900`}>{nfTRY.format(p.revenue)}</td>
                      <td className={tdRight}>
                        {p.revDeltaPct == null
                          ? <span className="text-gray-300">—</span>
                          : <span className={p.revDeltaPct >= 0 ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>
                              {p.revDeltaPct >= 0 ? '+' : ''}{p.revDeltaPct.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
                            </span>}
                      </td>
                    </tr>
                  );
                }}
              />
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Regions / iller */}
              <Card title="İllere Göre Trafik & Satış" icon={MapPin} hint="GA4 region = il">
                <Table
                  columns={[
                    { label: 'İl' }, { label: 'Oturum', align: 'right' }, { label: 'Pay', align: 'right' },
                    { label: 'Sipariş', align: 'right' }, { label: 'Ciro', align: 'right' },
                  ]}
                  rows={data.regions}
                  render={(r, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('font-medium text-gray-800')}>{r.region || '(bilinmiyor)'}</td>
                      <td className={tdRight}>{nfInt.format(r.sessions)}</td>
                      <td className={`${tdRight} text-gray-500`}>{pct(r.sessionShare)}</td>
                      <td className={tdRight}>{nfInt.format(r.transactions)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(r.revenue)}</td>
                    </tr>
                  )}
                />
              </Card>

              {/* Cities */}
              <Card title="Şehirlere Göre" icon={Building2}>
                <Table
                  columns={[
                    { label: 'Şehir' }, { label: 'Oturum', align: 'right' },
                    { label: 'Sipariş', align: 'right' }, { label: 'Ciro', align: 'right' },
                  ]}
                  rows={data.cities}
                  render={(c, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('font-medium text-gray-800')}>{c.city || '(bilinmiyor)'}</td>
                      <td className={tdRight}>{nfInt.format(c.sessions)}</td>
                      <td className={tdRight}>{nfInt.format(c.transactions)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(c.revenue)}</td>
                    </tr>
                  )}
                />
              </Card>
            </div>

            {/* Channels — aylık ciro pivotu */}
            <Card title="Trafik Kanalları — Aylık Ciro" icon={Radio} hint="ay = sütun · metrik: ciro">
              <Table
                columns={[
                  { label: 'Kanal' },
                  ...data.monthCols.map((m) => ({ label: m.label, align: 'right' })),
                  { label: 'Toplam', align: 'right' },
                ]}
                rows={data.channelsMonthly}
                render={(c, i) => (
                  <tr key={i} className="hover:bg-gray-50/60">
                    <td className={td('font-medium text-gray-800')}>{c.channel || '(bilinmiyor)'}</td>
                    {c.revenue.map((v, j) => (
                      <td key={j} className={tdRight}>{nfTRY.format(v)}</td>
                    ))}
                    <td className={`${tdRight} font-semibold text-gray-900`}>{nfTRY.format(c.total)}</td>
                  </tr>
                )}
              />
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top products */}
              <Card title="En Çok Satan Ürünler" icon={Package} hint="ciroya göre">
                <Table
                  columns={[{ label: 'Ürün' }, { label: 'Adet', align: 'right' }, { label: 'Ciro', align: 'right' }]}
                  rows={data.products}
                  render={(p, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('text-gray-800')}>
                        <span className="text-gray-400 mr-2">{i + 1}.</span>{p.name || '(bilinmiyor)'}
                      </td>
                      <td className={tdRight}>{nfInt.format(p.qty)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(p.revenue)}</td>
                    </tr>
                  )}
                />
              </Card>

              {/* Categories / product groups */}
              <Card title="Öne Çıkan Ürün Grupları" icon={Layers} hint="kategori">
                <Table
                  columns={[{ label: 'Grup' }, { label: 'Adet', align: 'right' }, { label: 'Ciro', align: 'right' }]}
                  rows={data.categories}
                  render={(c, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('text-gray-800')}>
                        <span className="text-gray-400 mr-2">{i + 1}.</span>{c.name || '(bilinmiyor)'}
                      </td>
                      <td className={tdRight}>{nfInt.format(c.qty)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(c.revenue)}</td>
                    </tr>
                  )}
                />
              </Card>
            </div>

            {/* İl × ürün grubu — satılan adet pivotu */}
            <Card title="İllere Göre Ürün Grubu — Satılan Adet" icon={MapPin} hint="satır = il · sütun = ürün grubu · metrik: adet">
              <Table
                columns={[
                  { label: 'İl' },
                  ...data.productGroupsByRegion.groups.map((g) => ({ label: g, align: 'right' })),
                  { label: 'Toplam', align: 'right' },
                ]}
                rows={data.productGroupsByRegion.rows}
                render={(r, i) => (
                  <tr key={i} className="hover:bg-gray-50/60">
                    <td className={td('font-medium text-gray-800')}>{r.region || '(bilinmiyor)'}</td>
                    {r.qty.map((q, j) => (
                      <td key={j} className={`${tdRight} ${q ? '' : 'text-gray-300'}`}>{nfInt.format(q)}</td>
                    ))}
                    <td className={`${tdRight} font-semibold text-gray-900`}>{nfInt.format(r.total)}</td>
                  </tr>
                )}
              />
            </Card>

            {/* Devices */}
            <Card title="Cihaz Kırılımı" icon={Smartphone}>
              <Table
                columns={[{ label: 'Cihaz' }, { label: 'Oturum', align: 'right' }, { label: 'Ciro', align: 'right' }]}
                rows={data.devices}
                render={(d, i) => (
                  <tr key={i} className="hover:bg-gray-50/60">
                    <td className={td('font-medium text-gray-800 capitalize')}>{d.device || '(bilinmiyor)'}</td>
                    <td className={tdRight}>{nfInt.format(d.sessions)}</td>
                    <td className={`${tdRight} font-medium`}>{nfTRY.format(d.revenue)}</td>
                  </tr>
                )}
              />
            </Card>

            {/* Engagement KPI kartları — yeni alanlar */}
            {(data.totals?.avgSessionDuration != null || data.totals?.bounceRate != null) && (
              <Card title="Oturum Kalitesi" icon={Activity}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">
                  {data.totals?.avgSessionDuration != null && (
                    <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200/70 p-4">
                      <div className="text-[11px] font-medium text-emerald-700/70 mb-1">Ort. Oturum Süresi</div>
                      <div className="text-lg font-bold text-gray-900">
                        {(() => {
                          const secs = Math.round(Number(data.totals.avgSessionDuration) || 0);
                          const m = Math.floor(secs / 60);
                          const s = secs % 60;
                          return m > 0 ? `${m}dk ${s}sn` : `${s}sn`;
                        })()}
                      </div>
                    </div>
                  )}
                  {data.totals?.bounceRate != null && (
                    <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200/70 p-4">
                      <div className="text-[11px] font-medium text-rose-700/70 mb-1">Çıkma Oranı</div>
                      <div className="text-lg font-bold text-gray-900">{pct(data.totals.bounceRate)}</div>
                    </div>
                  )}
                  {data.totals?.engagementRate != null && (
                    <div className="rounded-xl bg-sky-50 ring-1 ring-sky-200/70 p-4">
                      <div className="text-[11px] font-medium text-sky-700/70 mb-1">Etkileşim Oranı</div>
                      <div className="text-lg font-bold text-gray-900">{pct(data.totals.engagementRate)}</div>
                    </div>
                  )}
                  {data.totals?.engagedSessions != null && (
                    <div className="rounded-xl bg-violet-50 ring-1 ring-violet-200/70 p-4">
                      <div className="text-[11px] font-medium text-violet-700/70 mb-1">Etkileşimli Oturum</div>
                      <div className="text-lg font-bold text-gray-900">{nfInt.format(data.totals.engagedSessions)}</div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Mac & iPad İzolasyonu — talebin 1. maddesi */}
            {data.macIpadIsolation && (
              <Card title="Mac & iPad İzolasyonu — Genel Toplam İçindeki Payı" icon={Apple} hint="adet · ciro · toplam ciroya pay · Ağu→Eyl ivme">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
                  {[
                    { k: 'mac', label: 'Mac (MacBook + masaüstü)', accent: 'sky' },
                    { k: 'ipad', label: 'iPad', accent: 'violet' },
                    { k: 'combined', label: 'Mac + iPad (birlikte)', accent: 'emerald' },
                  ].map(({ k, label, accent }) => {
                    const g = data.macIpadIsolation[k];
                    const mom = g.augSepMomentum;
                    return (
                      <div key={k} className={`rounded-xl bg-gradient-to-b ring-1 ${ACCENT[accent]} p-4`}>
                        <div className="text-[11px] font-medium opacity-70 mb-1">{label}</div>
                        <div className="text-2xl font-bold text-gray-900">{pct(g.revShareOfTotal)}</div>
                        <div className="text-[11px] text-gray-500 mb-2">toplam ciro payı</div>
                        <div className="flex items-center justify-between text-xs text-gray-600 border-t border-gray-200/60 pt-2">
                          <span>{nfInt.format(g.qty)} adet</span>
                          <span className="font-medium">{nfTRY.format(g.revenue)}</span>
                        </div>
                        {mom != null && (
                          <div className="mt-1.5 text-[11px]">
                            <span className="text-gray-400">Ağu→Eyl adet: </span>
                            <span className={mom >= 0 ? 'text-emerald-700 font-semibold' : 'text-rose-600 font-semibold'}>
                              {mom >= 0 ? '+' : ''}{mom.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {data.insights?.notes?.macIpad && <NoteCallout>{data.insights.notes.macIpad}</NoteCallout>}
              </Card>
            )}

            {/* Ürün Grubu × Ay (Mac/iPad odaklı) */}
            {data.productGroupsMonthly?.rows?.length > 0 && (
              <Card
                title="Ürün Grubu × Ay (Mac/iPad odaklı)"
                icon={BarChart2}
                hint={`Ürün cirosu toplamı: ${nfTRY.format(data.productGroupsMonthly.itemRevenueTotal)}`}
              >
                <Table
                  columns={[
                    { label: 'Ürün Grubu' },
                    ...(data.productGroupsMonthly.months || []).map((m) => ({ label: m.label, align: 'right' })),
                    { label: 'Toplam Adet', align: 'right' },
                    { label: 'Toplam Ciro', align: 'right' },
                    { label: 'Ciro Payı', align: 'right' },
                  ]}
                  rows={data.productGroupsMonthly.rows}
                  render={(r, i) => {
                    const highlighted = ['iPad', 'MacBook', 'Mac', 'Apple Pencil'].includes(r.group);
                    return (
                      <tr key={i} className="hover:bg-gray-50/60">
                        <td className={td(highlighted ? 'font-medium text-gray-900' : 'text-gray-800')}>
                          {r.group || '(bilinmiyor)'}
                        </td>
                        {(r.qty || []).map((q, j) => (
                          <td key={j} className={`${tdRight} ${highlighted ? 'font-medium' : ''} ${q ? '' : 'text-gray-300'}`}>
                            {nfInt.format(q)}
                          </td>
                        ))}
                        <td className={`${tdRight} ${highlighted ? 'font-medium text-gray-900' : ''}`}>
                          {nfInt.format(r.totalQty)}
                        </td>
                        <td className={`${tdRight} ${highlighted ? 'font-semibold text-gray-900' : 'font-medium'}`}>
                          {nfTRY.format(r.totalRevenue)}
                        </td>
                        <td className={`${tdRight} text-gray-500`}>
                          {pct(r.revenueShareOfTotal)}
                        </td>
                      </tr>
                    );
                  }}
                />
              </Card>
            )}

            {/* Landing Page Performansı */}
            {data.landingPages?.length > 0 && (
              <Card title="Landing Page Performansı" icon={MousePointer} hint="ilk 15 sayfa · GA4 oturum bazlı">
                <Table
                  columns={[
                    { label: 'Sayfa' },
                    { label: 'Ziyaret', align: 'right' },
                    { label: 'Ort. Süre', align: 'right' },
                    { label: 'Çıkma %', align: 'right' },
                    { label: 'Etkileşim %', align: 'right' },
                    { label: 'İşlem', align: 'right' },
                    { label: 'Ciro', align: 'right' },
                  ]}
                  rows={data.landingPages.slice(0, 15)}
                  render={(lp, i) => {
                    const secs = Math.round(Number(lp.avgDuration) || 0);
                    const m = Math.floor(secs / 60);
                    const s = secs % 60;
                    const durStr = m > 0 ? `${m}dk ${s}sn` : `${s}sn`;
                    return (
                      <tr key={i} className="hover:bg-gray-50/60">
                        <td className={td('text-gray-800 max-w-xs truncate')} title={lp.lp}>
                          <span className="text-gray-400 mr-2">{i + 1}.</span>
                          {lp.lp || '/'}
                        </td>
                        <td className={tdRight}>{nfInt.format(lp.sessions)}</td>
                        <td className={tdRight}>{durStr}</td>
                        <td className={`${tdRight} text-rose-600`}>{pct(lp.bounceRate)}</td>
                        <td className={`${tdRight} text-emerald-700`}>{pct(lp.engagementRate)}</td>
                        <td className={tdRight}>{nfInt.format(lp.transactions)}</td>
                        <td className={`${tdRight} font-medium`}>{nfTRY.format(lp.revenue)}</td>
                      </tr>
                    );
                  }}
                />
              </Card>
            )}

            {/* BTS Landing Page Deep-Dive — talep maddesi 2 + 3 */}
            {data.lpDeepDive?.totals?.sessions > 0 && (
              <Card
                title={`BTS Landing Page Deep-Dive — "${data.lpDeepDive.pattern}"`}
                icon={MousePointer}
                hint="LP segmentine giren kullanıcılar: şehir · ne satın alıyor · toplam Mac/iPad'e oranı"
              >
                {/* LP özet metrikleri */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5 border-b border-gray-100">
                  {[
                    { label: 'Oturum', val: nfInt.format(data.lpDeepDive.totals.sessions) },
                    { label: 'Sipariş', val: nfInt.format(data.lpDeepDive.totals.transactions) },
                    { label: 'Dönüşüm', val: pct(data.lpDeepDive.totals.conversionRate) },
                    { label: 'Çıkma (bounce)', val: pct(data.lpDeepDive.totals.bounceRate) },
                    { label: 'Ort. Süre', val: fmtDur(data.lpDeepDive.totals.avgSessionDuration) },
                  ].map((m, i) => (
                    <div key={i} className="rounded-lg bg-gray-50 ring-1 ring-gray-200/70 p-3">
                      <div className="text-[11px] text-gray-500">{m.label}</div>
                      <div className="text-base font-bold text-gray-900">{m.val}</div>
                    </div>
                  ))}
                </div>

                {/* Toplam Mac/iPad'e oranı — vurgu */}
                <div className="grid grid-cols-2 gap-3 px-5 pt-4">
                  <div className="rounded-lg bg-sky-50 ring-1 ring-sky-200/70 p-3 text-center">
                    <div className="text-[11px] text-sky-700/70">Toplam Mac satışının bu LP'den kapanan payı</div>
                    <div className="text-xl font-bold text-gray-900">{pct(data.lpDeepDive.macShareOfTotalMac)}</div>
                    <div className="text-[11px] text-gray-400">{nfInt.format(data.lpDeepDive.lpMacQty)} adet</div>
                  </div>
                  <div className="rounded-lg bg-violet-50 ring-1 ring-violet-200/70 p-3 text-center">
                    <div className="text-[11px] text-violet-700/70">Toplam iPad satışının bu LP'den kapanan payı</div>
                    <div className="text-xl font-bold text-gray-900">{pct(data.lpDeepDive.ipadShareOfTotalIpad)}</div>
                    <div className="text-[11px] text-gray-400">{nfInt.format(data.lpDeepDive.lpIpadQty)} adet</div>
                  </div>
                </div>

                {/* MacBook & iPad: genel satış vs eğitim sayfasına uğrayan — kıyaslamalı */}
                {data.lpDeepDive.buyerCompare?.length > 0 && (
                  <div className="px-5 pt-5">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1.5 mb-2">
                      <Package className="w-3.5 h-3.5" /> MacBook &amp; iPad — Genel Satış vs Eğitim Sayfasına Uğrayanlar
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                            <th className="text-left px-3 py-2 font-semibold">Ürün</th>
                            <th className="text-right px-3 py-2 font-semibold">Genel Satış (adet)</th>
                            <th className="text-right px-3 py-2 font-semibold">Genel Ciro</th>
                            <th className="text-right px-3 py-2 font-semibold">Eğitim'e Uğrayan (adet)</th>
                            <th className="text-right px-3 py-2 font-semibold">Eğitim'e Uğrayan Ciro</th>
                            <th className="text-right px-3 py-2 font-semibold">Pay</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {data.lpDeepDive.buyerCompare.map((b, i) => (
                            <tr key={i} className="hover:bg-gray-50/60">
                              <td className={td('font-semibold text-gray-800')}>{b.group}</td>
                              <td className={tdRight}>{nfInt.format(b.totalQty)}</td>
                              <td className={tdRight}>{nfTRY.format(b.totalRev)}</td>
                              <td className={`${tdRight} font-medium`}>{nfInt.format(b.lpQty)}</td>
                              <td className={tdRight}>{nfTRY.format(b.lpRev)}</td>
                              <td className={`${tdRight} font-bold ${b.group === 'MacBook' ? 'text-sky-700' : 'text-violet-700'}`}>{pct(b.qtyShare)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1.5 italic">
                      "Eğitim'e uğrayan" = satın alınan oturum egitim sayfasından girmiş (landing). Pay = eğitim-temaslı adet / genel satış adedi.
                      GA4 son-tık olduğundan bu, ilgili ürünü <strong>alan ve eğitim sayfasıyla temas eden</strong> oturumların alt kümesidir.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-100 mt-4">
                  {/* Şehir kırılımı */}
                  <div className="bg-white">
                    <div className="px-5 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" /> İl (LP oturumları)
                    </div>
                    <Table
                      columns={[{ label: 'İl' }, { label: 'Oturum', align: 'right' }, { label: 'Pay', align: 'right' }, { label: 'Sipariş', align: 'right' }]}
                      rows={data.lpDeepDive.cities}
                      render={(c, i) => (
                        <tr key={i} className="hover:bg-gray-50/60">
                          <td className={td('font-medium text-gray-800')}>{c.region || '(bilinmiyor)'}</td>
                          <td className={tdRight}>{nfInt.format(c.sessions)}</td>
                          <td className={`${tdRight} text-gray-500`}>{pct(c.sessionShare)}</td>
                          <td className={tdRight}>{nfInt.format(c.transactions)}</td>
                        </tr>
                      )}
                    />
                  </div>
                  {/* Ne satın alıyor — modeller */}
                  <div className="bg-white">
                    <div className="px-5 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5" /> Ne satın alıyor (model · adet)
                    </div>
                    <Table
                      columns={[{ label: 'Model' }, { label: 'Adet', align: 'right' }, { label: 'Ciro', align: 'right' }]}
                      rows={data.lpDeepDive.topModels.slice(0, 8)}
                      render={(m, i) => (
                        <tr key={i} className="hover:bg-gray-50/60">
                          <td className={td('text-gray-800 max-w-[14rem] truncate')} title={m.name}>{m.name || '(bilinmiyor)'}</td>
                          <td className={tdRight}>{nfInt.format(m.qty)}</td>
                          <td className={`${tdRight} font-medium`}>{nfTRY.format(m.revenue)}</td>
                        </tr>
                      )}
                    />
                  </div>
                </div>

                {/* Kaynak kırılımı: LP'ye Google'dan mı Meta'dan mı geliniyor */}
                {data.lpDeepDive.channelSplit?.length > 0 && (
                  <div className="px-5 pt-5">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1.5 mb-2">
                      <TrendingUp className="w-3.5 h-3.5" /> LP'ye gelen trafik — kaynak (Google vs Meta)
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {data.lpDeepDive.channelSplit.map((b, i) => (
                        <div
                          key={i}
                          className={`rounded-lg ring-1 p-3 text-center ${
                            b.bucket === 'Google' ? 'bg-blue-50 ring-blue-200/70'
                              : b.bucket === 'Meta' ? 'bg-indigo-50 ring-indigo-200/70'
                                : 'bg-gray-50 ring-gray-200/70'
                          }`}
                        >
                          <div className="text-[11px] text-gray-500">{b.bucket}</div>
                          <div className="text-xl font-bold text-gray-900">{pct(b.sessionShare)}</div>
                          <div className="text-[11px] text-gray-400">{nfInt.format(b.sessions)} oturum · {nfInt.format(b.transactions)} sipariş</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1.5 italic">
                      GA4 son-tık kaynak/medya bazında, egitim LP oturumlarının payı.
                    </div>
                  </div>
                )}

                {/* LP'ye trafik getiren kampanyalar — platform bazında toplam */}
                {data.lpDeepDive.campaignSummary?.length > 0 && (
                  <div className="px-5 pt-5 pb-1">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1.5 mb-1">
                      <MousePointer className="w-3.5 h-3.5" /> LP'ye trafik getiren kampanyalar — platform toplamı
                    </div>
                    <Table
                      columns={[
                        { label: 'Platform' }, { label: 'Kampanya', align: 'right' },
                        { label: 'Oturum', align: 'right' }, { label: 'Pay', align: 'right' },
                        { label: 'Dönüşüm', align: 'right' }, { label: 'Ciro', align: 'right' },
                        { label: 'Harcama', align: 'right' }, { label: 'ROAS', align: 'right' },
                      ]}
                      rows={data.lpDeepDive.campaignSummary}
                      render={(c, i) => (
                        <tr key={i} className="hover:bg-gray-50/60">
                          <td className={td('')}>
                            <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              c.platform === 'Google' ? 'bg-blue-100 text-blue-700'
                                : c.platform === 'Meta' ? 'bg-indigo-100 text-indigo-700'
                                  : 'bg-gray-100 text-gray-500'
                            }`}>{c.platform}</span>
                          </td>
                          <td className={`${tdRight} text-gray-500`}>{nfInt.format(c.campaignCount)}</td>
                          <td className={tdRight}>{nfInt.format(c.sessions)}</td>
                          <td className={`${tdRight} text-gray-500`}>{pct(c.sessionShare)}</td>
                          <td className={tdRight}>{c.conversions != null ? nfInt.format(c.conversions) : '—'}</td>
                          <td className={tdRight}>{c.revenue != null ? nfTRY.format(c.revenue) : '—'}</td>
                          <td className={`${tdRight} font-medium`}>{c.spend != null ? nfTRY.format(c.spend) : '—'}</td>
                          <td className={tdRight}>{c.roas != null ? `${c.roas.toFixed(1)}x` : '—'}</td>
                        </tr>
                      )}
                    />
                    <div className="text-[11px] text-gray-400 mt-1.5 italic">
                      <strong>Oturum/Pay</strong> = egitim LP'ye o platformun kampanyalarından gelen GA4 trafiği.
                      <strong> Dönüşüm · Ciro · Harcama</strong> = bu kampanyaların platform-raporlu dönem <strong>toplamıdır</strong> (kampanya adına göre tekilleştirilir; yalnızca LP'ye özel değil — kampanyalar başka sayfalara da trafik/satış getirir). ROAS = ciro / harcama (platform-raporlu).
                      "Diğer" (organik/direkt) için platform kampanya verisi yoktur.
                    </div>
                  </div>
                )}
                {data.insights?.notes?.lp && <NoteCallout>{data.insights.notes.lp}</NoteCallout>}
              </Card>
            )}

            {/* Meta vs Google */}
            {data.metaVsGoogle?.length > 0 && (
              <Card title="Meta vs Google (Kaynak/Medya)" icon={TrendingUp}>
                <div className="px-5 pt-3.5 pb-1 text-[11px] text-gray-500 italic">
                  GA4 son-tık atıf — Meta talep yaratır ama satış çoğunlukla son-tıkta Google/Direkt'e yazılır, bu yüzden Meta düşük görünebilir.
                </div>
                <Table
                  columns={[
                    { label: 'Kanal' },
                    { label: 'Oturum', align: 'right' },
                    { label: 'İşlem', align: 'right' },
                    { label: 'Ciro', align: 'right' },
                    { label: 'Ciro Payı', align: 'right' },
                  ]}
                  rows={data.metaVsGoogle}
                  render={(b, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('font-medium text-gray-800')}>{b.bucket || '(bilinmiyor)'}</td>
                      <td className={tdRight}>{nfInt.format(b.sessions)}</td>
                      <td className={tdRight}>{nfInt.format(b.transactions)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(b.revenue)}</td>
                      <td className={`${tdRight} text-gray-500`}>{pct(b.revenueShare)}</td>
                    </tr>
                  )}
                />
              </Card>
            )}

            {/* Kanal × Mac/iPad — talep maddesi 4 */}
            {data.channelProductMatrix?.length > 0 && (
              <Card title="Kanal × Mac/iPad — Dönüşüm Kırılımı" icon={Split} hint="GA4 son-tık · kanal grubuna göre Mac & iPad adet/ciro + dönüşüm oranı">
                <Table
                  columns={[
                    { label: 'Kanal' },
                    { label: 'Mac adet', align: 'right' }, { label: 'Mac ciro', align: 'right' },
                    { label: 'iPad adet', align: 'right' }, { label: 'iPad ciro', align: 'right' },
                    { label: 'Oturum', align: 'right' }, { label: 'Dönüşüm', align: 'right' },
                  ]}
                  rows={data.channelProductMatrix}
                  render={(c, i) => {
                    const paid = ['Paid Search', 'Paid Social', 'Cross-network', 'Display', 'Video', 'Paid Other', 'Paid Shopping'].includes(c.channel);
                    return (
                      <tr key={i} className="hover:bg-gray-50/60">
                        <td className={td('font-medium text-gray-800')}>
                          {c.channel}
                          {paid && <span className="ml-2 text-[10px] rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 align-middle">paid</span>}
                          {c.channel === 'Cross-network' && <span className="ml-1 text-[10px] rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5 align-middle">PMax</span>}
                        </td>
                        <td className={tdRight}>{nfInt.format(c.macQty)}</td>
                        <td className={`${tdRight} text-gray-600`}>{nfTRY.format(c.macRev)}</td>
                        <td className={tdRight}>{nfInt.format(c.ipadQty)}</td>
                        <td className={`${tdRight} text-gray-600`}>{nfTRY.format(c.ipadRev)}</td>
                        <td className={`${tdRight} text-gray-500`}>{nfInt.format(c.sessions)}</td>
                        <td className={tdRight}>{pct(c.conversionRate)}</td>
                      </tr>
                    );
                  }}
                />
              </Card>
            )}

            {/* Paid kampanyalar — Cross-network/PMax ayrımı */}
            {data.paidCampaigns?.length > 0 && (
              <Card title="Ücretli Kampanyalar — Cross-network / PMax Ayrımı" icon={Radio} hint="GA4 son-tık · ciroya göre ilk 15">
                <Table
                  columns={[
                    { label: 'Kampanya' }, { label: 'Kanal' },
                    { label: 'Oturum', align: 'right' }, { label: 'Sipariş', align: 'right' },
                    { label: 'Dönüşüm', align: 'right' }, { label: 'Ciro', align: 'right' },
                  ]}
                  rows={data.paidCampaigns}
                  render={(c, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('text-gray-800 max-w-[16rem] truncate')} title={c.campaign}>{c.campaign}</td>
                      <td className={td('text-gray-600')}>
                        {c.isPMax
                          ? <span className="text-[11px] rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5">PMax / Cross-network</span>
                          : <span className="text-[11px] text-gray-500">{c.channel}</span>}
                      </td>
                      <td className={tdRight}>{nfInt.format(c.sessions)}</td>
                      <td className={tdRight}>{nfInt.format(c.transactions)}</td>
                      <td className={`${tdRight} text-gray-500`}>{pct(c.conversionRate)}</td>
                      <td className={`${tdRight} font-medium`}>{nfTRY.format(c.revenue)}</td>
                    </tr>
                  )}
                />
              </Card>
            )}

            {/* FAZ 2 — Harcama & ROAS + Bütçe Önerisi (raporun karar bölümü) */}
            {data.adSpend && (data.adSpend.google || data.adSpend.meta) && (
              <Card title="Faz 2 — Reklam Harcaması & ROAS" icon={DollarSign} hint="canlı: Google Ads API + Meta Graph API · dönem toplamı">
                <div className="px-5 pt-3.5 pb-1 text-[11px] text-gray-500 italic">
                  İki ROAS gösteriliyor: <b>platform-ROAS</b> (her platformun kendi atfı, optimize ettiği metrik) ve <b>GA4 son-tık ROAS</b> (gerçeklik kontrolü). Meta'da ikisi çok ayrışır — Meta talep yaratır, satış son-tıkta Google/Direkt'e yazılır.
                </div>
                <Table
                  columns={[
                    { label: 'Kanal' }, { label: 'Harcama', align: 'right' },
                    { label: 'Platform Ciro', align: 'right' }, { label: 'Platform ROAS', align: 'right' },
                    { label: 'GA4 Son-tık ROAS', align: 'right' },
                  ]}
                  rows={[
                    data.adSpend.google && { ...data.adSpend.google, channel: 'Google Ads' },
                    data.adSpend.meta && { ...data.adSpend.meta, channel: 'Meta Ads' },
                  ].filter(Boolean)}
                  render={(r, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className={td('font-medium text-gray-800')}>{r.channel}</td>
                      <td className={`${tdRight} font-semibold text-gray-900`}>{nfTRY.format(r.spend)}</td>
                      <td className={`${tdRight} text-gray-600`}>{nfTRY.format(r.platformRevenue)}</td>
                      <td className={tdRight}>
                        <span className={`font-semibold ${r.platformRoas >= 3 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtX(r.platformRoas)}</span>
                      </td>
                      <td className={tdRight}>
                        <span className={`${r.ga4Roas >= 3 ? 'text-emerald-700' : 'text-amber-600'}`}>{fmtX(r.ga4Roas)}</span>
                      </td>
                    </tr>
                  )}
                />
                {/* Google tip kırılımı (Search vs PMax) */}
                {data.adSpend.google?.byType && (
                  <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap gap-2 text-[11px]">
                    <span className="text-gray-400">Google harcama tipi:</span>
                    {Object.entries(data.adSpend.google.byType).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([t, v]) => (
                      <span key={t} className="rounded bg-gray-100 text-gray-700 px-2 py-0.5">
                        {t === 'PERFORMANCE_MAX' ? 'PMax' : t}: {nfTRY.format(v)}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Bizim bütçe kararımız — raporun finali */}
            {data.insights?.budget?.available && (
              <section className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-md overflow-hidden">
                <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
                  <Target className="w-5 h-5" />
                  <h2 className="text-base font-semibold">Bütçe Önerimiz — ROAS'a Dayalı Karar</h2>
                </div>
                <div className="px-6 py-5 space-y-3">
                  {data.insights.budget.verdict && (
                    <div className="rounded-xl bg-white/10 ring-1 ring-white/20 px-4 py-3 text-[15px] font-medium leading-relaxed">
                      {data.insights.budget.verdict}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {data.insights.budget.lines.map((l, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-emerald-50">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/70 shrink-0" />
                        <span>{l}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-emerald-100/70 pt-1">
                    Karar kuralı: her iki kanal &lt;3x → önce optimize · aksi halde ROAS'ı yüksek kanala yüklen, diğerini rolünde tut.
                  </p>
                </div>
              </section>
            )}

            {/* Arama Hacmi (BTS) */}
            {data.searchVolumes && (
              <Card
                title="Arama Hacmi (BTS) — Google Ads Keyword Planner"
                icon={Search}
                hint={`${data.searchVolumes.geo || ''} · ${data.searchVolumes.source || ''}`}
              >
                <Table
                  columns={[
                    { label: 'Ürün' },
                    { label: `${data.searchVolumes.baselineMonth ? `${['','Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'][data.searchVolumes.baselineMonth] || 'Baz'} (baz)` : 'Baz'}`, align: 'right' },
                    ...(data.searchVolumes.periodMonths || []).map((mn) => ({
                      label: ['','Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'][mn] || `Ay ${mn}`,
                      align: 'right',
                    })),
                    { label: 'Tem→Eyl', align: 'right' },
                    { label: 'Haz→Eyl', align: 'right' },
                    { label: 'Geçen Yıl Eyl', align: 'right' },
                  ]}
                  rows={data.searchVolumes.rows || []}
                  render={(r, i) => {
                    const formatSV = (v) => v == null ? '-' : nfInt.format(v);
                    const changePctEl = r.changePct == null ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <span className={Number(r.changePct) >= 0 ? 'text-emerald-700 font-medium' : 'text-rose-600 font-medium'}>
                        {Number(r.changePct) >= 0 ? '+' : ''}{(Number(r.changePct)).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
                      </span>
                    );
                    const vsBl = r.vsBaselinePct == null ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <span className={Number(r.vsBaselinePct) >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                        {Number(r.vsBaselinePct) >= 0 ? '+' : ''}{(Number(r.vsBaselinePct)).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
                      </span>
                    );
                    const yoy = r.yoyPct == null ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <span className={Number(r.yoyPct) >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                        {Number(r.yoyPct) >= 0 ? '+' : ''}{(Number(r.yoyPct)).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%
                      </span>
                    );
                    return (
                      <tr key={i} className="hover:bg-gray-50/60">
                        <td className={td('font-medium text-gray-800')}>{r.label || r.keyword}</td>
                        <td className={tdRight}>
                          {r.available === false ? <span className="text-gray-400 text-[11px]">veri yok</span> : formatSV(r.baseline?.value)}
                        </td>
                        {(r.months || []).map((mn, j) => (
                          <td key={j} className={tdRight}>
                            {r.available === false ? <span className="text-gray-400 text-[11px]">veri yok</span> : formatSV(mn.value)}
                          </td>
                        ))}
                        <td className={tdRight}>{r.available === false ? <span className="text-gray-400 text-[11px]">-</span> : changePctEl}</td>
                        <td className={tdRight}>{r.available === false ? <span className="text-gray-400 text-[11px]">-</span> : vsBl}</td>
                        <td className={tdRight}>{r.available === false ? <span className="text-gray-400 text-[11px]">-</span> : yoy}</td>
                      </tr>
                    );
                  }}
                />
              </Card>
            )}

            <p className="text-xs text-gray-400 text-center pt-2">
              Kaynak: Google Analytics 4 (canlı sorgu) · {data.period.start} → {data.period.end} · DB'ye yazılmaz
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
